import { ScaleElement } from '@scale/base-element';
import { Events, FiltersFailedEvent, ToastEvent } from '@scale/events';
import { morph, MORPH_DEFAULTS } from '@scale/morph';
import { activateScripts, fetchTextWithTimeout, parseSectionHTML, rollText } from '@scale/utilities';

class ScaleCollectionGrid extends ScaleElement {
  static observeRefs = true;
  #controller;
  #scrollObserver;
  #loadingMore = false;
  #renderedUrl;
  #countText;

  setup() {
    this.#renderedUrl = window.location.href;
    this.#countText = this.ref('product-count')?.textContent.trim();
    this.onDocument(Events.FILTERS_UPDATED, (e) => this.#handleFilterUpdate(e));

    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }
  }

  teardown() {
    this.#controller?.abort();
    this.#scrollObserver?.disconnect();
  }

  updatedCallback() {
    this.invalidateRefs();
    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }
  }


  async #handleFilterUpdate(event) {
    this.#setLoading(true);
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    try {
      const page = await this.#fetchPage(event.url, controller.signal);
      if (!this.#replaceProducts(page)) throw new Error('Malformed collection response');
      this.#updateFacets(page);
      this.#renderedUrl = new URL(event.url, window.location.origin).toString();
      requestAnimationFrame(() => this.#scrollToGrid());
      this.#announceCount();
      this.#rollCount();
    } catch (err) {
      if (err?.name !== 'AbortError' && this.#controller === controller) {
        history.replaceState({}, '', this.#renderedUrl);
        this.broadcast(new FiltersFailedEvent());
        this.broadcast(new ToastEvent(this.dataset.failedMessage, { type: 'error' }));
      }
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        this.#setLoading(false);
      }
    }
  }


  handlePageClick(event) {
    event.preventDefault();
    const url = event.target.closest('a')?.href;
    if (!url) return;

    history.pushState({}, '', url);
    this.#renderPage(url);
  }

  async loadMore(event) {
    if (this.#loadingMore) return;
    const btn = event.target.closest('button');
    const url = btn?.dataset.nextUrl;
    if (!url) return;

    this.#loadingMore = true;
    btn.setAttribute('aria-busy', 'true');
    let appended = false;

    try {
      const page = await this.#fetchPage(url);
      const items = this.#appendProducts(page);
      if (!items) throw new Error('Malformed collection response');
      appended = true;
      if (btn.matches(':focus')) {
        items[0].tabIndex = -1;
        items[0].focus();
      }
      history.replaceState({}, '', url);
      this.#renderedUrl = new URL(url, window.location.origin).toString();
    } catch {
    } finally {
      btn.removeAttribute('aria-busy');
      this.#loadingMore = false;
      if (appended && this.dataset.paginationType === 'infinite') this.#observeSentinel();
    }
  }


  #observeSentinel() {
    this.#scrollObserver?.disconnect();

    const sentinel = this.ref('sentinel');
    if (!sentinel) return;

    this.#scrollObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#loadNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    this.#scrollObserver.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loadingMore) return;
    this.#loadingMore = true;
    this.#scrollObserver?.disconnect();

    const pagination = this.ref('pagination');
    const btn = pagination?.querySelector('[data-next-url]');
    const url = btn?.dataset.nextUrl;
    if (!url) {
      this.#loadingMore = false;
      return;
    }
    let appended = false;

    try {
      const page = await this.#fetchPage(url);
      if (!this.#appendProducts(page)) throw new Error('Malformed collection response');
      appended = true;
      history.replaceState({}, '', url);
      this.#renderedUrl = new URL(url, window.location.origin).toString();
    } catch {
      this.#retryInfiniteOnNextScroll();
    } finally {
      this.#loadingMore = false;
      if (appended && this.dataset.paginationType === 'infinite' && this.ref('sentinel')) {
        this.#observeSentinel();
      }
    }
  }


  async #renderPage(url) {
    this.#setLoading(true);
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    try {
      const page = await this.#fetchPage(url, controller.signal);
      if (!this.#replaceProducts(page)) throw new Error('Malformed collection response');
      this.#renderedUrl = new URL(url, window.location.origin).toString();
      requestAnimationFrame(() => this.#scrollToGrid());
    } catch (err) {
      if (err?.name !== 'AbortError' && this.#controller === controller) {
        history.replaceState({}, '', this.#renderedUrl);
      }
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        this.#setLoading(false);
      }
    }
  }

  async #fetchPage(url, signal = this.signal) {
    const sectionId = this.dataset.sectionId;
    const fetchUrl = new URL(url, window.location.origin);
    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.has('preview_theme_id') && !fetchUrl.searchParams.has('preview_theme_id')) {
      fetchUrl.searchParams.set('preview_theme_id', currentUrl.searchParams.get('preview_theme_id'));
    }

    fetchUrl.searchParams.set('section_id', sectionId);

    const { response, text } = await fetchTextWithTimeout(fetchUrl, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return parseSectionHTML(text);
  }

  #replaceProducts(page) {
    const incoming = page.querySelector('[data-ref="products"]');
    const current = this.ref('products');
    if (!incoming || !current) return false;

    const products = document.importNode(incoming, true);
    current.replaceWith(products);
    activateScripts(products);
    this.invalidateRefs();
    this.ref('grid')?.setAttribute('data-entering', '');

    if (this.dataset.paginationType === 'infinite') {
      this.#observeSentinel();
    }

    return true;
  }

  #updateFacets(page) {
    const current = this.querySelector('scale-facets');
    const incoming = page.querySelector('scale-facets');
    if (current && incoming) morph(current, incoming, MORPH_DEFAULTS);
  }

  #appendProducts(page) {
    const newGrid = page.querySelector('[data-ref="grid"]');
    const grid = this.ref('grid');
    if (!newGrid || !grid) return null;

    const items = [...newGrid.children].map((item) => document.importNode(item, true));
    for (const item of items) item.setAttribute('data-entering', '');
    grid.append(...items);
    for (const item of items) activateScripts(item);

    const newPagination = page.querySelector('[data-ref="pagination"]');
    const pagination = this.ref('pagination');
    if (pagination && newPagination) {
      morph(pagination, newPagination, MORPH_DEFAULTS);
      this.announce(newPagination.dataset.loadedMessage, 'polite');
    } else if (pagination && !newPagination) {
      pagination.remove();
    }

    this.invalidateRefs();

    const newCount = page.querySelector('[data-ref="product-count"]');
    const count = this.ref('product-count');
    if (count && newCount) count.textContent = newCount.textContent;

    return items;
  }


  #setLoading(loading) {
    this.ref('grid')?.toggleAttribute('data-loading', loading);
    this.toggleAttribute('data-loading', loading);
    if (loading) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');

    for (const facets of document.querySelectorAll('scale-facets')) {
      facets.toggleAttribute('data-loading', loading);
      if (loading) facets.setAttribute('aria-busy', 'true');
      else facets.removeAttribute('aria-busy');
    }
  }

  #retryInfiniteOnNextScroll() {
    window.addEventListener('scroll', () => this.#observeSentinel(), {
      once: true,
      passive: true,
      signal: this.signal,
    });
  }

  #scrollToGrid() {
    const toolbar = this.ref('toolbar');
    if (toolbar) {
      toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #rollCount() {
    const count = this.ref('product-count');
    if (!count) return;
    const previous = this.#countText;
    const next = count.textContent.trim();
    this.#countText = next;
    if (next === previous) return;

    const was = previous.match(/^(\D*)(\d+)(\D*)$/);
    const now = next.match(/^(\D*)(\d+)(\D*)$/);
    const direction = Number(now?.[2]) < Number(was?.[2]) ? -1 : 1;
    const roll = document.createElement('span');
    roll.className = 'collection-toolbar__roll';

    if (was && now && was[1] === now[1] && was[3] === now[3]) {
      roll.textContent = was[2];
      count.replaceChildren(now[1], roll, now[3]);
      rollText(roll, now[2], direction);
    } else {
      roll.textContent = previous;
      count.replaceChildren(roll);
      rollText(roll, next, direction);
    }
  }

  #announceCount() {
    const count = this.ref('product-count');
    if (count) {
      this.announce(count.textContent, 'polite');
    }
  }
}

if (!customElements.get('scale-collection-grid')) {
  customElements.define('scale-collection-grid', ScaleCollectionGrid);
}
